import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthApiError, refreshSession } from '../services/authApi';
import {
  clearStoredAuthTokens,
  getStoredAuthTokens,
  hasSessionHint,
  subscribeToStoredAuthTokens,
  StoredAuthTokens,
} from '../services/authTokenStorage';

type AuthSessionContextValue = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  tokens: StoredAuthTokens | null;
  restoreSession: () => Promise<boolean>;
  clearSession: () => void;
};

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

export const AuthSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tokens, setTokens] = useState<StoredAuthTokens | null>(() => getStoredAuthTokens());
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToStoredAuthTokens(setTokens);
    return unsubscribe;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      // Skip the refresh API call entirely if no session hint exists.
      // This prevents a guaranteed 401 console error for every unauthenticated page load.
      if (!hasSessionHint()) {
        if (isMounted) setIsInitializing(false);
        return;
      }
      try {
        await refreshSession();
      } catch (error) {
        if (!(error instanceof AuthApiError && error.statusCode === 401)) {
          void error;
        }
        clearStoredAuthTokens();
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    void bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<AuthSessionContextValue>(() => ({
    isInitializing,
    isAuthenticated: Boolean(tokens?.idToken),
    tokens,
    restoreSession: async () => {
      try {
        await refreshSession();
        return Boolean(getStoredAuthTokens()?.idToken);
      } catch (error) {
        if (!(error instanceof AuthApiError && error.statusCode === 401)) {
          void error;
        }
        clearStoredAuthTokens();
        return false;
      }
    },
    clearSession: () => {
      clearStoredAuthTokens();
    },
  }), [isInitializing, tokens]);

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  );
};

export function useAuthSession() {
  const context = useContext(AuthSessionContext);
  if (!context) {
    throw new Error('useAuthSession must be used within AuthSessionProvider.');
  }
  return context;
}
